import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import {
  Layers,
  Wrench,
  DollarSign,
  Shield,
  CheckCircle2,
  ChevronDown,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/mp-foundation-repair-paramus-nj-041326.webp";

const faqs = [
  {
    question: "How do I know if my foundation crack is structural or just cosmetic?",
    answer:
      "The width of a crack can tell you a lot. A cosmetic crack often looks like a thin, hairline line in a poured concrete wall, a common issue caused by normal concrete shrinking. However, anything wider than 1/4 inch should be looked at by a professional. If you notice a crack that is growing in size or letting in water, do not wait. Horizontal cracks or stair-step cracks in block walls may signify structural issues.",
  },
  {
    question: "How much does foundation repair cost in North Jersey?",
    answer:
      "Most foundation crack repairs range from $500 to $3,500 depending on length, type, and access. Bowing wall repairs using carbon fiber straps can run $4,000 to $10,000. Full waterproofing systems start around $8,000 based on linear footage and sump pump requirements. New construction pours are priced per project. We provide written estimates before any work starts.",
  },
  {
    question: "Does foundation repair require a permit in New Jersey?",
    answer:
      "Typically, structural foundation repairs and new foundation pours require a permit in New Jersey. Minor crack injection repairs often do not. Requirements vary by municipality across Passaic, Bergen, Essex, and Morris Counties. We pull the required permits for every job where they apply, so you do not have to manage that process yourself.",
  },
  {
    question: "How long does foundation repair last?",
    answer:
      "An epoxy crack injection repair is permanent when the underlying cause is addressed. With proper drainage, waterproofing membrane systems can last 20 to 30 years. Carbon fiber strap installations do not degrade and carry long warranties.",
  },
  {
    question: "Can I finish my basement after foundation repair or waterproofing?",
    answer:
      "Yes. Once waterproofing and any structural repairs are complete and have been inspected, you can finish out the basement. Our recommendation is to wait at least 30 days after waterproofing membrane installation before framing interior walls.",
  },
];

const citations = [
  {
    id: 1,
    text: "Rutgers New Jersey Agricultural Experiment Station — NJ Soil Survey — Clay soil composition and expansion characteristics across North Jersey counties.",
    url: "https://njaes.rutgers.edu/",
  },
  {
    id: 2,
    text: "National Ready Mixed Concrete Association — Freeze-Thaw Resistance of Concrete — Concrete mix specifications for freeze-thaw exposure climates.",
    url: "https://www.nrmca.org/",
  },
  {
    id: 3,
    text: "Carbon Fiber Strap Foundation Repair — Basement Systems Technical Library — Carbon fiber strap installation standards and warranty documentation.",
    url: "https://www.basementsystems.com/",
  },
  {
    id: 4,
    text: "New Jersey Department of Community Affairs — New Jersey Uniform Construction Code — Permit requirements for structural and foundation work in New Jersey municipalities.",
    url: "https://www.nj.gov/dca/divisions/codes/",
  },
  {
    id: 5,
    text: "New Jersey Geological and Water Survey — Bedrock Geology of New Jersey — Shale and glacial till distribution across Morris and Passaic Counties.",
    url: "https://www.nj.gov/dep/njgs/",
  },
  {
    id: 6,
    text: "International Concrete Repair Institute — Epoxy Injection Guidelines — Epoxy and polyurethane injection standards for structural crack repair.",
    url: "https://www.icri.org/",
  },
];

export default function FoundationRepair() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Concrete Foundation Repair Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="North Jersey foundations face clay soil, freeze-thaw cycles, and aging block walls. We fix them right across Bergen, Essex, Passaic, and Morris Counties." />
        <meta property="og:title" content="Concrete Foundation Repair Totowa, NJ | Masonry Pro" />
        <meta property="og:description" content="North Jersey foundations face clay soil, freeze-thaw cycles, and aging block walls. We fix them right across Bergen, Essex, Passaic, and Morris Counties." />
        <meta name="twitter:title" content="Foundation Repair NJ - Cracks, Bowing, Leaks | Masonry Pro" />
        <meta name="twitter:description" content="Worried about a foundation crack in your North Jersey home? We tell you if it's cosmetic or structural before any work starts. Free assessment." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Foundation Repair",
            description: "Home foundation repair in North Jersey for clay soil, freeze-thaw cycles, and aging block walls.",
            url: "/concrete-services/foundation-repair",
            image: "https://masonry-pro.com/og/foundation-repair.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/foundation-repair.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/foundation-repair.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Concrete foundation repair in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Concrete Foundation Repair in Totowa, NJ Serving North Jersey:{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>
                    Built on 35+ Years in the Field
                  </span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  You've heard the phrase: your home is only as strong as its foundation. From new construction to crack repair, slab lifting to waterproofing, we work in concrete foundation repair across <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> Counties every day. Biagio started this company after 30 years as a union bricklayer. His son Gino runs the jobs in the field. You call or text us, we show up, and we tell you exactly what we see.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="/contact">Get a Free Site Evaluation</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Soil Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                      <Layers className="h-6 w-6 text-secondary" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold">
                      North Jersey Soil and Foundations.{" "}
                      <span className="text-gradient-gold">What Could Go Wrong?</span>
                    </h2>
                  </div>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Did you know the North Jersey soil under your house may be part of the problem? North Jersey has clay, shale, and shifting soil depending on what area you live in.{" "}
                      <a
                        href="https://njaes.rutgers.edu/"
                        className="text-secondary hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Clay causes the most trouble
                      </a>
                      <sup>¹</sup>. It soaks up water and pushes outward, then dries out and pulls back. Every season, your foundation absorbs that movement.
                    </p>
                    <p>
                      Biagio knows North Jersey soil. He can tell you a wet basement in Wayne usually isn't the same problem as one in Montclair. The soil is different, the water table is different, and the fix is different.
                    </p>
                    <p>
                      We look at where water collects, how old the foundation is, and what the ground is actually doing. That determines the right repair, not a one-size-fits-all solution.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={heroImage}
                    alt="Concrete foundation repair in Woodland Park, Passaic County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Materials Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                    <Wrench className="h-6 w-6 text-secondary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    The Materials Make a Difference for{" "}
                    <span className="text-gradient-gold">North Jersey Winters</span>
                  </h2>
                </div>
                <p className="text-lg text-muted-foreground mb-6">
                  North Jersey winters can be hard on concrete. Cheap materials fail where cold winters can vary wildly and the freeze-thaw cycle can force water into every small crack. Each method we use has a specific purpose, and using the wrong material makes the problem worse.
                </p>
                <p className="text-lg font-semibold mb-4">Here is what we use and why:</p>
                <ul className="space-y-3 text-muted-foreground">
                  {[
                    <>
                      <a href="https://www.nrmca.org/" className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer">Durable, cold-weather-rated concrete mix</a><sup>²</sup> for freeze-thaw cycles common across Passaic and Morris Counties
                    </>,
                    "Epoxy injection for structural cracks that require reinforcement",
                    "Polyurethane foam injection for non-structural cracks",
                    <>
                      <a href="https://www.basementsystems.com/" className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer">Carbon fiber straps</a><sup>³</sup> for bowing or leaning basement walls
                    </>,
                    "Quick setting cement for fast-setting repairs on actively leaking walls",
                    "Drainage board and waterproof membrane on new construction pours and retrofits",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Cost Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-secondary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Cost of Foundation Repair in{" "}
                    <span className="text-gradient-gold">North Jersey</span>
                  </h2>
                </div>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    No two foundation jobs are exactly alike, so costs can vary widely based on what your home needs, the different materials required, or the additional labor needed to get the job done.
                  </p>
                  <p>
                    A homeowner in Wayne dealing with a small hairline crack will spend far less on repairs than someone in Montclair facing a flooded basement. A licensed engineer who specializes in structural and foundational issues may need to be called in for these types of problems. This adds cost but protects your investment by addressing the root cause and gives future buyers confidence in the work done.
                  </p>
                  <p>
                    We provide estimates that break down each step so you understand exactly what you're paying for. No surprises.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* North Jersey Challenges Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                    <Shield className="h-6 w-6 text-secondary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    What North Jersey Homeowners Deal With That{" "}
                    <span className="text-gradient-gold">Other Regions Don't</span>
                  </h2>
                </div>
                <p className="text-lg text-muted-foreground mb-6">
                  Older homes across Bergen, Essex, Passaic, and Morris Counties most likely still sit on the original poured concrete or block foundations built around the 1940s, never waterproofed to modern standards. Add 30 to 80 years of freeze-thaw cycles and you have a foundation that is working hard just to stay in place.
                </p>
                <p className="text-lg font-semibold mb-4">Foundation issues we see regularly across North Jersey:</p>
                <ul className="space-y-3 text-muted-foreground">
                  {[
                    "Clay soil expansion: most common in Montclair, Glen Ridge, and the Watchung areas. During rainy seasons, clay holds water and pushes against foundation walls.",
                    "High water tables: low-lying areas in Passaic County near the Passaic River basin, and parts of southern Bergen County near the Hackensack River, experience groundwater pressure that forces water through block and poured concrete walls.",
                    <>
                      <a href="https://www.nj.gov/dep/njgs/" className="text-secondary hover:underline" target="_blank" rel="noopener noreferrer">Shale bedrock</a><sup>⁵</sup>: mostly in the northwestern part of the state. Shale affects footing depth and determines how stable the soil is under your foundation.
                    </>,
                    <>Older block foundations: homes in Wayne, <Link to="/service-areas/passaic-county/clifton" className="text-secondary hover:underline">Clifton</Link>, and Paterson often have unreinforced block walls that crack and bow over time.</>,
                    "New construction challenges: custom built homes in western Morris County and northern Passaic County require careful foundation prep as the ground can vary significantly.",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-secondary" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Foundation Services{" "}
                    <span className="text-gradient-gold">We Provide</span>
                  </h2>
                </div>
                <p className="text-lg text-muted-foreground mb-8">
                  We take time to diagnose the real problem before we start any work. That means the right fix the first time.
                </p>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-background rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3 text-foreground">For New Construction</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      {[
                        "Full foundation pours planned for your specific home design",
                        "Walkout basement configurations for sloped lots in Morris County",
                        "Energy-efficient foundations for custom builds",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-1.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-background rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3 text-foreground">Repair &amp; Stabilization</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      {[
                        "Epoxy and polyurethane crack injection for structural and water-related cracks",
                        "Carbon fiber strap installation for bowing walls",
                        "Slab lifting and leveling using foam injection for settled concrete",
                        "Full wall replacement for foundations beyond repair",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-1.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-background rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3 text-foreground">Waterproofing</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      {[
                        "French drain systems with sump pump installation",
                        "Exterior waterproofing during new pours or excavation repairs",
                        "Crack routing and sealing for minor surface water intrusion",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-1.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Step-by-Step Process */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto bg-card rounded-xl p-8 border border-border">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">
                  How Concrete Foundation Repair Works: Our Step-by-Step Approach
                </h2>
                <p className="text-muted-foreground mb-8">
                  Most residential foundation repairs in North Jersey take one to three days.
                </p>
                <ol className="space-y-5">
                  {[
                    {
                      title: "Initial Review",
                      desc: "We visit the property and evaluate the foundation, soil conditions, and drainage.",
                    },
                    {
                      title: "Locate the Problem",
                      desc: "Is the issue structural, water-related, or both? We explain what we find and encourage you to ask questions.",
                    },
                    {
                      title: "Written Estimate",
                      desc: "You will receive a clear, itemized estimate before any work begins, covering labor, materials, and timeline.",
                    },
                    {
                      title: "Site Prep",
                      desc: "We protect your property and prep the work area before any concrete or repair material is placed.",
                    },
                    {
                      title: "Repair or New Pour",
                      desc: "Depending on the issue, we may repair by slab lifting, wall stabilization, or full foundation pour.",
                    },
                    {
                      title: "Waterproofing and Drainage",
                      desc: "We address water invasion (either interior or exterior) and redirect or prevent future damage.",
                    },
                    {
                      title: "Final Walkthrough",
                      desc: "We walk the finished job with you before we close out. You see exactly what was done and why.",
                    },
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-gradient-navy flex items-center justify-center flex-shrink-0 text-secondary font-bold text-sm">
                        {i + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{step.title}</p>
                        <p className="text-muted-foreground">{step.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>

          {/* Category hub backlink */}
          <section className="py-8 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-muted-foreground">
                  Foundation repair is one piece of our <Link to="/concrete-services" className="text-secondary hover:underline">concrete services in North Jersey</Link>, and we handle new pours and finishing too.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq) => (
                  <div key={faq.question} className="bg-background rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="concrete" />

          {/* CTA Section */}
          <CTASection
            heading="Ready to Fix Your"
            highlightedHeading="Foundation the Right Way?"
            serviceName="concrete foundation repair"
          />

          {/* Citations */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      <span className="flex items-center gap-2">
                        Citations &amp; References
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
                        {citations.map((citation) => (
                          <li key={citation.id}>
                            {citation.text}{" "}
                            <a
                              href={citation.url}
                              className="text-secondary hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {citation.url}
                            </a>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
